/**
 * Sky's own settings — her look, her voice, and what she may reach beyond the
 * platform. Shown in two places: the tabs of her panel, and the Settings
 * screen, which is where somebody looks who cannot find them in the panel.
 * One implementation, so the two cannot disagree about what a setting does.
 */
import { useEffect, useMemo, useState } from "react";
import s from "./assistant.module.css";
import { AssistantMark } from "./AssistantMark";
import { useAssistant } from "./AssistantContext";
import { useSession } from "../app/SessionContext";
import {
  atLeast,
  grantedLevel,
  LEVEL_LABEL,
  LEVEL_WHAT,
  LEVELS,
  readAccessOverride,
  useAccessLevel,
  writeAccessOverride,
  type AccessLevel,
} from "./access";
import * as learning from "./learning";
import { principalKey } from "./preferences";
import { MODEL_PROVIDERS, SEARCH_PROVIDERS, hasModel, platformModel, providerOf } from "./external";
import { DEFAULTS, type MarkVariant } from "./preferences";
import { approxBytes, downscaleToDataUrl } from "./downscale";
import * as voice from "./voice";
import * as ears from "./listen";
import { Button, Field, Input, Select } from "../design/components";
import { t, tn, tc } from "../i18n";

/* ── personalisation ──────────────────────────────────────────────────── */

const VARIANTS: { id: MarkVariant; label: string; note: string }[] = [
  {
    id: "avatar",
    label: "Her",
    note: "A drawn presence: she blinks, follows your pointer, and her lips move while she speaks.",
  },
  { id: "pulse", label: "Pulse", note: "An abstract mark. A core that breathes, three points, a traveling arc." },
  { id: "orbit", label: "Orbit", note: "The same mark with the three points circling instead of holding." },
  { id: "arc", label: "Arc", note: "Ring and arc only — the quietest of the three." },
  {
    id: "image",
    label: "Your own image",
    note: "A photograph, in the same 3 × 2 card with the blue field and the grid. It cannot move its lips.",
  },
];

export function Look() {
  const a = useAssistant();
  const [imageNote, setImageNote] = useState<string | null>(null);

  return (
    <div className={s.settings}>
      <p className={s.settingsNote}>
        {t(
          "Just for you, saved with your sign-in in this browser. The default is the platform's own mark.",
        )}
      </p>

      <div className={s.settingsRow}>
        <span className={s.settingsLabel}>{t("Mark")}</span>
        {VARIANTS.map((v) => (
          <label key={v.id} className={s.reminderRow}>
            <input
              type="radio"
              name="mark"
              checked={a.prefs.mark === v.id}
              onChange={() => a.setPrefs({ ...a.prefs, mark: v.id })}
            />
            <AssistantMark state="idle" size={22} variant={v.id} image={a.prefs.markImage} />
            <span>
              <span className={s.suggestionLabel}>{t(v.label)}</span>
              <span className={s.suggestionTrade}>{t(v.note)}</span>
            </span>
          </label>
        ))}
      </div>

      {a.prefs.mark === "image" && (
        <div className={s.settingsRow}>
          <span className={s.settingsLabel}>{t("Image")}</span>
          <input
            type="file"
            accept="image/*"
            aria-label={t("Choose an image")}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setImageNote(t("Resizing…"));
              /* Resized here rather than refused: a phone photograph is three
                 or four megabytes, and the machine can fix that in a moment. */
              downscaleToDataUrl(f)
                .then((dataUrl) => {
                  a.setPrefs({ ...a.prefs, markImage: dataUrl });
                  setImageNote(t("Loaded — {kb} KB after resizing.", { kb: Math.round(approxBytes(dataUrl) / 1024) }));
                })
                .catch((err: Error) => setImageNote(err.message));
            }}
          />
          {imageNote && <span className={s.settingsHint}>{imageNote}</span>}
          <span className={s.settingsHint}>
            {t(
              "JPEG or PNG, landscape 3:2 (864 × 576 is ideal). Any size is resized to fit. iPhone HEIC photos can't be read: export as JPEG first.",
            )}
          </span>
          {a.prefs.markImage && (
            <Button size="sm" variant="ghost" onClick={() => a.setPrefs({ ...a.prefs, markImage: undefined })}>
              {t("Remove image")}
            </Button>
          )}
        </div>
      )}

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          checked={a.prefs.greet}
          onChange={(e) => a.setPrefs({ ...a.prefs, greet: e.target.checked })}
        />
        <span>{t("Greet me when I arrive")}</span>
      </label>

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          checked={a.prefs.quiet}
          onChange={(e) => a.setPrefs({ ...a.prefs, quiet: e.target.checked })}
        />
        <span>{t("Keep quiet unless I ask")}</span>
      </label>
      <span className={s.settingsHint}>
        {t(
          "Quiet stops the badge, reading aloud and new alerts while you work. Everything she works out still goes to the log in her panel.",
        )}
      </span>

      <div>
        <Button size="sm" variant="ghost" onClick={() => a.setPrefs({ ...DEFAULTS, voice: { ...DEFAULTS.voice } })}>
          {t("Back to the default")}
        </Button>
      </div>
    </div>
  );
}

export function VoiceSettings() {
  const a = useAssistant();
  const [avail, setAvail] = useState<voice.VoiceAvailability | null>(null);

  useEffect(() => {
    let live = true;
    voice.availability().then((v) => live && setAvail(v));
    return () => {
      live = false;
    };
  }, []);

  const voices = avail?.state === "ready" ? avail.voices : [];

  return (
    <div className={s.settings}>
      <p className={s.settingsNote}>
        {t(
          "She says hello and goodbye out loud. Otherwise she's silent unless you press “Read aloud” or turn on commentary below.",
        )}
      </p>

      {avail?.state === "unsupported" && (
        <p className={s.settingsHint}>{t("This browser has no speech synthesis, so nothing here will work in it.")}</p>
      )}
      {avail?.state === "no-voices" && (
        <p className={s.settingsHint}>
          {t(
            "This computer has no voices installed. Voices come from the operating system.",
          )}
        </p>
      )}

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          disabled={!ears.isSupported()}
          checked={a.prefs.voice.handsFree}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, handsFree: e.target.checked } })}
        />
        <span>{t("Listen while she is open, and answer what she hears")}</span>
      </label>
      <span className={s.settingsHint}>
        {t(
          "On: speak and she answers. Off: type only. She stops listening when you close her, go quiet or say “thanks”.",
        )}
      </span>

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          disabled={!ears.isSupported()}
          checked={a.prefs.voice.wake}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, wake: e.target.checked } })}
        />
        <span>{t("Listen for my voice while she is away")}</span>
      </label>
      <span className={s.settingsHint}>
        {ears.isSupported()
          ? t(
              "Say “assistant” to call her back. The microphone stays open with a recording indicator, and in Chrome the audio is sent away for transcription. The corner tab shows a dot while listening.",
            )
          : t("This browser has no speech recognition, so she cannot listen for her name in it.")}
      </span>

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          disabled={avail?.state !== "ready"}
          checked={a.prefs.voice.greetAloud}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, greetAloud: e.target.checked } })}
        />
        <span>{t("Say hello and goodbye out loud")}</span>
      </label>
      <span className={s.settingsHint}>
        {t(
          "One line at the start and end of a shift, after you enter or sign out. The browser can only play sound after a click.",
        )}
      </span>

      <label className={s.reminderRow}>
        <input
          type="checkbox"
          disabled={avail?.state !== "ready"}
          checked={a.prefs.voice.enabled}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, enabled: e.target.checked } })}
        />
        <span>{t("Read every answer aloud as it arrives")}</span>
      </label>
      <span className={s.settingsHint}>
        {t(
          "This is for typed questions; spoken questions always get spoken answers. Say “stop” to stop her reading.",
        )}
      </span>

      <div className={s.settingsRow}>
        <span className={s.settingsLabel}>{t("Voice")}</span>
        <Select
          disabled={avail?.state !== "ready"}
          value={a.prefs.voice.voiceURI}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, voiceURI: e.target.value } })}
        >
          <option value="">{t("System default")}</option>
          {voices.map((v) => (
            <option key={v.voiceURI} value={v.voiceURI}>
              {v.name} · {v.lang}
            </option>
          ))}
        </Select>
        <span className={s.settingsHint}>
          {voices.length
            ? tn(voices.length, "{n} installed on this machine.", "{n} installed on this machine.")
            : t("Voices come from the operating system.")}
        </span>
      </div>

      <div className={s.settingsRow}>
        <span className={s.settingsLabel}>{t("Hears")}</span>
        <Select
          value={a.prefs.voice.lang}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, lang: e.target.value } })}
        >
          <option value="">{t("Follow the voice above")}</option>
          {[...new Set(voices.map((v) => v.lang).filter(Boolean))].sort().map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </Select>
        <span className={s.settingsHint}>
          {t(
            "The language your speech is transcribed in. This is separate from her voice.",
          )}
        </span>
      </div>

      <Field label={t("Rate · {rate}×", { rate: a.prefs.voice.rate.toFixed(1) })}>
        <input
          type="range"
          min={0.6}
          max={1.6}
          step={0.1}
          value={a.prefs.voice.rate}
          onChange={(e) => a.setPrefs({ ...a.prefs, voice: { ...a.prefs.voice, rate: Number(e.target.value) } })}
        />
      </Field>

      <div>
        <Button
          size="sm"
          variant="ghost"
          disabled={avail?.state !== "ready"}
          onClick={() => a.readAloud("Runway one seven three five. Friction below minimum. Severity five.")}
        >
          {t("Hear it")}
        </Button>
      </div>
    </div>
  );
}

export function Sources() {
  const a = useAssistant();
  const cfg = a.config;
  const provider = useMemo(() => providerOf(cfg.modelProvider), [cfg.modelProvider]);

  return (
    <div className={s.settings}>
      <p className={s.settingsNote}>
        {t(
          "She always reads this platform's records; everything below is optional. Keys are kept only in this browser and aren't protected there.",
        )}
      </p>

      <div className={s.settingsRow}>
        <span className={s.settingsLabel}>{t("Language model")}</span>
        <Select value={cfg.modelProvider} onChange={(e) => a.setConfig({ ...cfg, modelProvider: e.target.value })}>
          {MODEL_PROVIDERS.map((p) => (
            <option key={p.id} value={p.id}>
              {t(p.label)}
            </option>
          ))}
        </Select>
        <span className={s.settingsHint}>{t(provider.hint)}</span>
      </div>

      {/* The platform's model has no key field, because there is no key to
          put in this browser. What it needs instead is to say whether the
          deployment actually holds one — otherwise "connected" and "nothing
          will happen" look identical from here. */}
      {cfg.modelProvider === "platform" ? (
        <p className={s.settingsHint}>
          {platformModel()
            ? t("This deployment holds a key and composes on your behalf. Model: {model}.", {
                model: platformModel()!.model,
              })
            : t(
                "This deployment holds no model key, or this copy is on sample data. Nothing is sent anywhere; she answers from what she can read.",
              )}
        </p>
      ) : (
        <Field label={provider.keyLabel}>
          <Input
            type="password"
            value={cfg.modelKey}
            onChange={(e) => a.setConfig({ ...cfg, modelKey: e.target.value })}
            placeholder={cfg.modelProvider === "ollama" ? t("usually blank") : t("paste the key")}
          />
        </Field>
      )}

      {cfg.modelProvider === "custom" && (
        <>
          <Field label="Endpoint">
            <Input value={cfg.endpoint} onChange={(e) => a.setConfig({ ...cfg, endpoint: e.target.value })} />
          </Field>
          <Field label="Model">
            <Input value={cfg.model} onChange={(e) => a.setConfig({ ...cfg, model: e.target.value })} />
          </Field>
        </>
      )}

      <div className={s.settingsRow}>
        <span className={s.settingsLabel}>{t("Web search")}</span>
        <Select value={cfg.searchProvider} onChange={(e) => a.setConfig({ ...cfg, searchProvider: e.target.value })}>
          {SEARCH_PROVIDERS.map((p) => (
            <option key={p.id} value={p.id}>
              {t(p.label)}
            </option>
          ))}
        </Select>
        <span className={s.settingsHint}>
          {(() => {
            const hint = SEARCH_PROVIDERS.find((p) => p.id === cfg.searchProvider)?.hint;
            return hint === undefined ? hint : t(hint);
          })()}
        </span>
      </div>

      <Field label="Search key">
        <Input
          type="password"
          value={cfg.searchKey}
          onChange={(e) => a.setConfig({ ...cfg, searchKey: e.target.value })}
          placeholder={t("optional")}
        />
      </Field>

      <p className={s.settingsHint}>
        {hasModel(cfg)
          ? t("A model is connected. She will say when an answer came from it.")
          : t("No model connected — she will answer from platform records and say so.")}
      </p>
    </div>
  );
}

/* ── the access level in force on this copy ───────────────────────────── */

export function Access() {
  const session = useSession();
  const who = principalKey(session.principal);
  const level = useAccessLevel(session.principal, who);
  const granted = grantedLevel(session.principal);
  const override = readAccessOverride(who);
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--muted)", maxWidth: "80ch" }}>
        {t(
          "Sky reads, says and opens only what's at or below this level. She won't list, open or discuss screens above it.",
        )}
      </p>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--muted)", maxWidth: "80ch" }}>
        {granted.issued ? (
          <>
            {t("The platform granted")} <b>{tc(LEVEL_LABEL[granted.level], "access")}</b>{" "}
            {t(
              "from the roles {name} holds. You can lower it here but not raise it. To change it, change the person’s roles on the platform.",
              { name: session.principal.name },
            )}
          </>
        ) : (
          <>
            <b>{t("This level was not issued by a platform.")}</b>{" "}
            {session.principal.method === "sample"
              ? t("This is the built-in sample, which shows the whole platform.")
              : t(
                  "The platform didn't return one, so it was read from your sign-in groups. It isn't a grant.",
                )}{" "}
            {t("It can still be lowered here.")}
          </>
        )}
      </p>
      <label style={{ display: "grid", gap: 4, fontSize: 13, color: "var(--muted)", maxWidth: 360 }}>
        {t("level in force")}
        <select
          value={override ?? ""}
          onChange={(e) => writeAccessOverride(who, (e.target.value || undefined) as AccessLevel | undefined)}
          aria-label={t("access level")}
        >
          <option value="">
            {granted.issued ? t("as granted") : t("as the sign-in implies")} — {tc(LEVEL_LABEL[granted.level], "access")}
          </option>
          {/* only what is at or below the grant; the rest cannot take effect and
              offering them would be a control that silently does nothing */}
          {LEVELS.filter((l) => atLeast(granted.level, l)).map((l) => (
            <option key={l} value={l}>
              {tc(LEVEL_LABEL[l], "access")}
              {l === granted.level ? "" : ` — ${t("lower than granted")}`}
            </option>
          ))}
        </select>
      </label>
      <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink)" }}>
        <b>{tc(LEVEL_LABEL[level], "access")}</b>: {t(LEVEL_WHAT[level])}.
      </p>
    </div>
  );
}

/* ── what she has learned ─────────────────────────────────────────────── */

export function Learning() {
  const session = useSession();
  const a = useAssistant();
  const who = principalKey(session.principal);
  const sum = learning.summary(who);
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--muted)", maxWidth: "80ch" }}>
        {t(
          "Each question and what answered it, the screens you taught her, and questions still unanswered.",
        )}
      </p>
      <p style={{ margin: 0, fontSize: 13.5 }}>
        {t("{asked} asked · {answered} answered · {taught} routes taught · {unanswered} still unanswered", {
          asked: sum.asked,
          answered: sum.answered,
          taught: sum.taught.length,
          unanswered: sum.unanswered.length,
        })}
      </p>
      {sum.taught.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, display: "grid", gap: 3 }}>
          {sum.taught.map((tr) => (
            <li key={tr.q}>
              “{tr.q}” → {tr.title} ({tr.times}×)
            </li>
          ))}
        </ul>
      )}
      {sum.unanswered.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "var(--muted)", display: "grid", gap: 3 }}>
          {sum.unanswered.map((u) => (
            <li key={u.q} data-testid="unanswered-row">
              “{u.q}” — {u.times}× · {t("open the screen that answers it and she will remember")}
            </li>
          ))}
        </ul>
      )}
      <div>
        <button
          type="button"
          onClick={() => {
            learning.forget(who, session.source);
            void a.ask("what have you learned");
          }}
          style={{
            font: "inherit",
            fontSize: 13,
            padding: "4px 10px",
            borderRadius: 5,
            border: "1px solid var(--line)",
            background: "var(--panel2)",
            color: "var(--ink)",
            cursor: "pointer",
          }}
        >
          {t("Forget what she has learned")}
        </button>
      </div>
    </div>
  );
}
